import { routerMiddleware } from 'connected-react-router';
import createSagaMiddleware from 'redux-saga';
import { createHashHistory } from 'history';
import { responsiveStoreEnhancer } from 'redux-responsive';
import { configureStore, TypedStartListening } from '@reduxjs/toolkit';
import { persistStore, persistReducer, createTransform } from 'redux-persist';
import storageSession from 'redux-persist/lib/storage/session';
import bmApiService, { PERSISTED_BM_API_ENDPOINTS } from '@/services/bmApi';

import createRootReducer from '@/store/reducers';
import sagas from '@/store/sagas';

import { authListener } from './store/reducers/newAuth';
import { formsListener } from './store/reducers/cancelLicenseQuestionaire';
import { resyncCodelockListener } from './store/reducers/resyncCodelock';
import { eaccountingListener } from './store/reducers/eaccounting';
import { correlationIdListener } from './store/reducers/correlationId';
import { rtkQueryErrorLogger } from './store/rtkLicenseMiddleware';
import { calculatePriceListener } from './store/reducers/calculatePrice'
import { stripeAuthListener } from './store/reducers/stripeAuthSlice'
import { incentivesListener } from './store/reducers/incentives';
import { similarServiceListener } from './store/reducers/similarServicesSlice';

declare global {
    interface Window {
        __REDUX_DEVTOOLS_EXTENSION_COMPOSE__?: any;
    }
}

export const history = createHashHistory();

export const createStoreWithInitialState = (initialState?: any) => {
    const rootReducer = createRootReducer(history);
    const reduxRouterMiddleware = routerMiddleware(history);
    const sagaMiddleware = createSagaMiddleware();

    // Persist only selected RTK Query cache entries in sessionStorage via redux-persist.
    const apiCacheTransform = createTransform(
        (inboundState: any, key: string | number) => {
            if (key !== bmApiService.reducerPath || !inboundState) {
                return inboundState;
            }

            const persistedEndpoints = new Set(PERSISTED_BM_API_ENDPOINTS as readonly string[]);

            const { mutations, queries = {}, ...rest } = inboundState;

            // Keep only queries whose endpointName is in our persisted allowlist.
            const filteredQueries: typeof queries = {};
            for (const cacheKey of Object.keys(queries)) {
                const querySubState = queries[cacheKey];
                if (querySubState && persistedEndpoints.has(querySubState.endpointName)) {
                    filteredQueries[cacheKey] = querySubState;
                }
            }

            return {
                ...rest,
                queries: filteredQueries,
                // Do not persist in-flight mutations.
                mutations: {},
            };
        },
        (outboundState: any) => outboundState,
        { whitelist: [bmApiService.reducerPath] }
    );

    const persistConfig = {
        key: 'root',
        storage: storageSession,
        whitelist: [bmApiService.reducerPath],
        transforms: [apiCacheTransform],
    };

    // redux-persist's generics are hard to reconcile with our legacy reducer typings.
    // Cast to `any` to prevent type errors from blocking builds.
    const persistedReducer = persistReducer(persistConfig as any, rootReducer as any) as any;

    const store = configureStore({
        preloadedState: initialState,
        reducer: persistedReducer as any,
        devTools: {
            name: `caui-${process.env.REACT_APP_ENVIRONMENT}`,
            actionsDenylist: [
                '@@localize',
                '@@router',
                '@@redux-form/UNREGISTER_FIELD',
                '@@redux-form/REGISTER_FIELD',
                '@@redux-form/INITIALIZE',
                '@@redux-form/FOCUS',
                '@@redux-form/BLUR',
                '@@redux-form/TOUCH',
                'RNS',
                'bmApi',
                'authenticate/*',
                'authorize/*',
                'loading-bar'
            ]

        },
        middleware: ((getDefaultMiddleware: any) => [
            ...getDefaultMiddleware({
                // TODO: try enabling this and check /newsletters/send
                immutableCheck: false,
                serializableCheck: false,
            }),
            incentivesListener.middleware,
            similarServiceListener.middleware,
            stripeAuthListener.middleware,
            calculatePriceListener.middleware,
            eaccountingListener.middleware,
            correlationIdListener.middleware,
            formsListener.middleware,
            authListener.middleware,
            resyncCodelockListener.middleware,
            bmApiService.middleware,
            reduxRouterMiddleware,
            sagaMiddleware,
            rtkQueryErrorLogger
        ]) as any,
        // @ts-ignore
        enhancers: [responsiveStoreEnhancer],
    });
    sagaMiddleware.run(sagas);

    if (process.env.NODE_ENV !== 'production') {
        if (module.hot) {
            module.hot.accept('./store/reducers', () => {
                store.replaceReducer(rootReducer);
            });
        }
    }

    return store;
};

export const store = createStoreWithInitialState();
export const persistor = persistStore(store);

// NOTE: We dispatch a mix of plain actions, redux-form-saga actions, RTK Query thunks, and async thunks.
// Keep dispatch typing permissive across the app.
export type ApplicationDispatch = any;
export const appDispatch: ApplicationDispatch = store.dispatch as any;

// NOTE: This project has many legacy reducers that are not fully typed.
// Using the inferred ReturnType<typeof store.getState> causes widespread "type {}" errors.
// Keep state typing permissive so `tsc` can typecheck the app without blocking development.
// If/when reducers are properly typed, this can be tightened again.
export type ApplicationState = any;

export type TypedListener = TypedStartListening<ApplicationState, ApplicationDispatch>;

